/**
 * MBMR readiness — shared vocabulary for the rail, the tree dots and the submit control.
 *
 * The four operation states and the three check statuses come straight from the
 * RDY-BE/RDY-FE contract (readiness_contract.md). Nothing here invents a value:
 * anything the server sends that is not in the contract is normalised to a safe
 * default so a backend drift cannot blank the editor.
 *
 * Colour rule: every state carries a GLYPH as well as a colour, and every dot gets a
 * `title`, so the states are readable without relying on hue (colour-blind users,
 * printouts, low-contrast screens). Colours themselves come from the antd theme
 * tokens (`@/themes/antd-theme`), never hard-coded hexes, so dark mode works.
 */
import { defaultColors } from '@/themes/antd-theme';

/** Contract states, worst → best. Used for the process roll-up precedence. */
export const STATE_ORDER = ['blocked', 'not_started', 'partial', 'ready'];

export const STATES = {
  ready: { key: 'ready', label: 'Ready', glyph: '✓', short: 'ready' },
  partial: { key: 'partial', label: 'Partially configured', glyph: '◐', short: 'partial' },
  blocked: { key: 'blocked', label: 'Blocked', glyph: '✕', short: 'blocked' },
  not_started: { key: 'not_started', label: 'Not started', glyph: '○', short: 'not started' },
};

export const CHECK_STATUS = {
  ok: { key: 'ok', glyph: '✓', label: 'Configured' },
  missing: { key: 'missing', glyph: '✕', label: 'Missing' },
  optional: { key: 'optional', glyph: '○', label: 'Optional for this operation type' },
};

/**
 * Lifecycle states — the record's APPROVAL life, which is a different axis from readiness.
 * Contract revision 3 (`lifecycle` block). Readiness answers "is the configuration complete";
 * lifecycle answers "has it been submitted, is it under review, was it returned, is it signed".
 * A record can be perfectly ready AND already approved — in which case there is nothing to
 * submit, and offering a submit button is the bug this vocabulary exists to fix.
 */
export const LIFECYCLE = {
  draft: { key: 'draft', label: 'Draft', tone: 'neutral', short: 'not yet submitted' },
  under_review: { key: 'under_review', label: 'Under review', tone: 'info', short: 'with reviewers' },
  partially_submitted: { key: 'partially_submitted', label: 'Partially submitted', tone: 'info', short: 'part under review' },
  returned: { key: 'returned', label: 'Returned for correction', tone: 'danger', short: 'returned' },
  approved: { key: 'approved', label: 'Approved', tone: 'success', short: 'approved' },
};

export const safeLifecycle = (state) => (LIFECYCLE[state] ? state : 'draft');

/** Theme-token colours per lifecycle tone. Never a raw hex — dark mode reads the same tokens. */
export const lifecycleColors = (state, isDark) => {
  const g = defaultColors.palette.gray;
  switch (LIFECYCLE[safeLifecycle(state)].tone) {
    case 'success':
      return { fg: isDark ? defaultColors.successLight : defaultColors.success, bg: isDark ? 'rgba(45,122,62,0.18)' : 'rgba(45,122,62,0.10)' };
    case 'danger':
      return { fg: isDark ? defaultColors.errorLight : defaultColors.error, bg: isDark ? 'rgba(185,28,28,0.20)' : 'rgba(185,28,28,0.09)' };
    case 'info':
      return { fg: isDark ? defaultColors.warningLight : defaultColors.warning, bg: isDark ? 'rgba(217,119,6,0.18)' : 'rgba(217,119,6,0.10)' };
    default:
      return { fg: isDark ? g[400] : g[500], bg: isDark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.08)' };
  }
};

/**
 * How the rail should frame a readiness finding, given where the record is in its life.
 * The findings themselves never change — an approved operation with no acceptance limit is
 * still an operation with no acceptance limit. Only the sentence around them changes.
 */
export const FINDING_FRAMING = {
  blocking: {
    key: 'blocking',
    title: 'Blocking submission',
    empty: 'Nothing is blocking submission.',
    note: '',
  },
  under_review: {
    key: 'under_review',
    title: 'Findings — with reviewers',
    empty: 'No findings against this record.',
    note: 'This record is under review. A reviewer who accepts these findings should return it for correction.',
  },
  post_approval: {
    key: 'post_approval',
    title: 'Findings against an approved record',
    empty: 'No findings against this approved record.',
    note: 'This MBMR is approved. These gaps are still real — an approved record with no acceptance limit on a step is a compliance finding, not noise — but they cannot be fixed by re-submitting. Return the record for correction, or raise a change control.',
  },
};

export const safeFraming = (framing) => (FINDING_FRAMING[framing] ? framing : 'blocking');

/**
 * Findings about the RECORD, not about any one operation (RV11, backend `record_findings`).
 *
 * Every other finding in this rail belongs to an operation and is rendered as a button that
 * navigates to it. These belong to none, so they must NOT be rendered that way — a row that
 * looks navigable and goes nowhere is worse than a row that plainly does not.
 *
 * The server sends `label`, `why` and `fix` already worded. This table adds only what the UI
 * needs and the payload cannot carry: where in the editor the author should go. An unknown
 * code still renders — with the server's own words and no destination — because a backend that
 * grows a finding must never blank the list.
 */
export const RECORD_FINDINGS = {
  ORDER_NOT_STATED: { where: 'Process flow', glyph: '⇄' },
  ORPHAN_OPERATION: { where: 'Process flow', glyph: '⇄' },
  UNPLACED_BOM_LINE: { where: 'Bill of materials', glyph: '⚗' },
  CARRY_FORWARD_LOST: { where: 'Bill of materials', glyph: '⚗' },
  // Advisory, and the only one that is: it reads the step's words against the rule's unit, so a
  // false positive must cost a glance and not a submission.
  RULE_MEASURES_SOMETHING_ELSE: { where: 'The step that carries the rule', glyph: '≠' },
};

/** The record-level findings, normalised. Never throws on a shape it does not recognise. */
export const recordFindings = (model) =>
  (Array.isArray(model?.record_findings) ? model.record_findings : [])
    .filter((f) => f && typeof f === 'object')
    .map((f) => ({
      code: String(f.code || 'FINDING'),
      blocking: f.severity !== 'advisory',
      label: f.label || 'This record has a finding',
      why: f.why || '',
      fix: f.fix || '',
      where: RECORD_FINDINGS[f.code]?.where || '',
      glyph: RECORD_FINDINGS[f.code]?.glyph || '!',
    }));

/**
 * Everything that blocks submission, operations and record together.
 *
 * `summary.blocking_count` counts OPERATIONS with a gap and always has. Reading it alone is
 * how the submit control came to offer a cheerful green "Submit for approval" on a record the
 * server would refuse — the button promised an action that could not happen.
 */
export const totalBlocking = (summary) =>
  (summary?.blocking_count ?? 0) + (summary?.record_blocking_count ?? 0);


/** A state the UI can always render, even if the server sends something unexpected. */
export const safeState = (state) => (STATES[state] ? state : 'not_started');
export const safeStatus = (status) => (CHECK_STATUS[status] ? status : 'missing');

/**
 * Theme-token colours per state. `isDark` picks the lighter variants that the rest of
 * the editor uses in dark mode (see defaultColors.*Light).
 */
export const stateColors = (state, isDark) => {
  const g = defaultColors.palette.gray;
  switch (safeState(state)) {
    case 'ready':
      return {
        fg: isDark ? defaultColors.successLight : defaultColors.success,
        bg: isDark ? 'rgba(45,122,62,0.18)' : 'rgba(45,122,62,0.10)',
      };
    case 'partial':
      return {
        fg: isDark ? defaultColors.warningLight : defaultColors.warning,
        bg: isDark ? 'rgba(217,119,6,0.18)' : 'rgba(217,119,6,0.10)',
      };
    case 'blocked':
      return {
        fg: isDark ? defaultColors.errorLight : defaultColors.error,
        bg: isDark ? 'rgba(185,28,28,0.20)' : 'rgba(185,28,28,0.09)',
      };
    default:
      return { fg: isDark ? g[400] : g[500], bg: 'transparent' };
  }
};

export const checkColors = (status, isDark) => {
  const g = defaultColors.palette.gray;
  switch (safeStatus(status)) {
    case 'ok':
      return isDark ? defaultColors.successLight : defaultColors.success;
    case 'missing':
      return isDark ? defaultColors.errorLight : defaultColors.error;
    default:
      return isDark ? g[400] : g[500];
  }
};

/** Worst-of roll-up, with the contract's one exception (ready + not_started ⇒ partial). */
export const rollUpState = (states) => {
  const list = (states || []).map(safeState);
  if (!list.length) return 'not_started';
  if (list.includes('blocked')) return 'blocked';
  if (list.includes('partial')) return 'partial';
  const hasReady = list.includes('ready');
  const hasNotStarted = list.includes('not_started');
  if (hasReady && hasNotStarted) return 'partial';
  return hasNotStarted ? 'not_started' : 'ready';
};

/**
 * Normalise a contract payload into exactly the shape the UI reads.
 * Defensive on purpose — RDY-BE is building this endpoint in parallel, so the rail must
 * survive a missing `summary`, a null `checks`, or an extra state value.
 */
/**
 * Normalise the contract's `lifecycle` block. A server that has not shipped it yet (or a
 * recipe whose approval tables could not be read) sends null, and the UI must degrade to
 * "we do not know" — which is NOT the same as "draft, go ahead and submit". `known: false`
 * is what the header reads to withhold both the submit offer and the return action rather
 * than guessing.
 */
export const normalizeLifecycle = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return {
      known: false,
      state: 'draft',
      label: 'Approval state unavailable',
      editable: true,
      editable_reason: '',
      submit_available: false,
      submit_unavailable_reason: 'The approval state of this MBMR could not be read, so submission is not offered.',
      submit_blocked_by: 'lifecycle',
      return_available: false,
      return_unavailable_reason: 'The approval state of this MBMR could not be read.',
      findings_framing: 'blocking',
      findings_count: 0,
      is_production: false,
      anomalies: [],
      chain: { configured: false },
      processes: [],
      byProcess: {},
    };
  }
  const processes = (Array.isArray(raw.processes) ? raw.processes : []).map((p) => ({
    process_id: p?.process_id,
    process_name: p?.process_name || '',
    status: p?.status || null,
    state: safeLifecycle(p?.state),
    editable: Boolean(p?.editable),
    returned: p?.returned || null,
  }));
  const byProcess = {};
  processes.forEach((p) => {
    byProcess[p.process_id] = p;
  });
  return {
    known: true,
    state: safeLifecycle(raw.state),
    label: raw.label || LIFECYCLE[safeLifecycle(raw.state)].label,
    editable: Boolean(raw.editable),
    editable_reason: raw.editable_reason || '',
    submit_available: Boolean(raw.submit_available),
    submit_unavailable_reason: raw.submit_unavailable_reason || '',
    // 'lifecycle' | 'readiness' | '' — which gate refused. A lifecycle refusal is final and
    // renders as a status; a readiness refusal is a to-do list and stays a button.
    submit_blocked_by: raw.submit_blocked_by || '',
    return_available: Boolean(raw.return_available),
    return_unavailable_reason: raw.return_unavailable_reason || '',
    findings_framing: safeFraming(raw.findings_framing),
    findings_count: Number.isFinite(raw.findings_count) ? raw.findings_count : 0,
    is_production: Boolean(raw.is_production),
    anomalies: Array.isArray(raw.anomalies) ? raw.anomalies : [],
    chain: raw.chain || { configured: false },
    production_release: raw.production_release || null,
    processes,
    byProcess,
  };
};

export const normalizeReadiness = (raw) => {
  // ── Shape tolerance (contract deviation, verified against the live endpoint) ──
  // The contract documents ONE response envelope, but the narrowed forms return the
  // bare node instead:
  //   ?recipe_id=    → { recipe_id, summary, processes: [...] }   (as documented)
  //   ?process_id=   → { process_id, process_name, state, operations: [...] }
  //   ?operation_id= → { operation_id, operation_name, state, checks: [...] }
  // Wrap the bare forms back into the envelope so every caller sees one shape.
  let d = raw || {};
  if (Array.isArray(d.checks) && d.operation_id !== undefined) {
    d = { processes: [{ process_id: null, process_name: '', operations: [d] }] };
  } else if (Array.isArray(d.operations) && d.process_id !== undefined) {
    d = { processes: [d] };
  }

  const processes = (Array.isArray(d.processes) ? d.processes : []).map((p) => {
    const operations = (Array.isArray(p.operations) ? p.operations : []).map((o) => {
      const checks = (Array.isArray(o.checks) ? o.checks : []).map((c) => ({
        key: c?.key || '',
        label: c?.label || '',
        status: safeStatus(c?.status),
        blocking: Boolean(c?.blocking),
        // 2026-10-29 · the instruction check names what lacks one: the operation itself and/or its steps.
        ...(Array.isArray(c?.missing_steps)
          ? { missing_steps: c.missing_steps, operation_missing: Boolean(c.operation_missing) }
          : {}),
      }));
      const blockingReasons =
        Array.isArray(o.blocking_reasons) && o.blocking_reasons.length
          ? o.blocking_reasons
          : checks.filter((c) => c.status === 'missing' && c.blocking).map((c) => c.label);
      return {
        operation_id: o?.operation_id,
        operation_number: o?.operation_number,
        operation_name: o?.operation_name || 'Untitled operation',
        operation_type: o?.operation_type || '',
        operation_type_id: o?.operation_type_id,
        profile: o?.profile || 'generic',
        state: safeState(o?.state),
        checks,
        blocking_reasons: blockingReasons,
      };
    });
    return {
      process_id: p?.process_id,
      process_name: p?.process_name || 'Untitled process',
      process_number: p?.process_number,
      // Trust the server's roll-up when it sent one, otherwise derive it.
      state: p?.state ? safeState(p.state) : rollUpState(operations.map((o) => o.state)),
      operations,
    };
  });

  const allOps = processes.flatMap((p) => p.operations);
  const count = (s) => allOps.filter((o) => o.state === s).length;
  const blocking = allOps.filter((o) => o.state === 'blocked');
  const s = d.summary || {};

  return {
    recipe_id: d.recipe_id,
    recipe_name: d.recipe_name || '',
    is_packaging: Boolean(d.is_packaging),
    lifecycle: normalizeLifecycle(d.lifecycle),
    processes,
    operations: allOps,
    // 2026-10-14 · kept, not only counted: `record_blocking_count` survived normalisation but the
    // findings themselves were dropped, so `recordFindings(model)` was always empty and the
    // designer's "Review issues" said "Nothing is blocking this record" beside "2 blocking".
    record_findings: Array.isArray(d.record_findings) ? d.record_findings : [],
    summary: {
      operations_total: Number.isFinite(s.operations_total) ? s.operations_total : allOps.length,
      ready: Number.isFinite(s.ready) ? s.ready : count('ready'),
      partial: Number.isFinite(s.partial) ? s.partial : count('partial'),
      blocked: Number.isFinite(s.blocked) ? s.blocked : count('blocked'),
      not_started: Number.isFinite(s.not_started) ? s.not_started : count('not_started'),
      blocking_count: Number.isFinite(s.blocking_count) ? s.blocking_count : blocking.length,
      // The record's own blocking findings (order, clearance profile, BOM, CPV …). Dropped here
      // until 2026-10-06, so the rail's "N record findings" and the stage bar's total never
      // counted them although they block submission like any operation gap.
      record_blocking_count: Number.isFinite(s.record_blocking_count) ? s.record_blocking_count : 0,
      // 2026-10-29 · operations, and the steps in them, with no instruction — these stop submit and approval.
      instruction_gaps: Number.isFinite(s.instruction_gaps) ? s.instruction_gaps : 0,
      steps_without_instruction: Number.isFinite(s.steps_without_instruction) ? s.steps_without_instruction : 0,
      submittable:
        typeof s.submittable === 'boolean'
          ? s.submittable
          : (Number.isFinite(s.blocking_count) ? s.blocking_count : blocking.length) === 0,
    },
  };
};

/** Flatten to { [operation_id]: operation } and { [process_id]: state } for O(1) tree lookups. */
export const buildIndex = (model) => {
  const byOperation = {};
  const byProcess = {};
  (model?.processes || []).forEach((p) => {
    byProcess[p.process_id] = p.state;
    p.operations.forEach((o) => {
      byOperation[o.operation_id] = { ...o, process_id: p.process_id, process_name: p.process_name };
    });
  });
  return { byOperation, byProcess };
};
